import { useEffect, useMemo, useRef, useState } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useNavigate } from '@tanstack/react-router'
import { Card, CardContent } from '@/components/ui/card'
import { Form } from '@/components/ui/form'
import { Button } from '@/components/ui/button'
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import {
  TextInputField,
  TextareaField,
  PriceInputField,
  NumberInputField,
  SelectField,
  FormSubmitButton,
} from '@/components/forms/FormComponents'
import { createProductSchema, updateProductSchema, type CreateProductData, type UpdateProductData } from '@/lib/form-schemas'
import { toastHelpers } from '@/lib/toast-helpers'
import { ProductVariantsSection } from './ProductVariantsSection'
import { ItemOptionPrices } from './ItemOptionPrices'
import { EMPTY_VARIANTS, basePriceFromSizes, draftProblem as variantsProblem, persistVariants, snapshot as variantsSnapshot, type VariantsState } from '@/lib/productVariants'
import { ProductIdentityHeader } from './product/ProductIdentityHeader'
import { ProductPhotoField } from './product/ProductPhotoField'
import { ProductGalleryField } from './product/ProductGalleryField'
import { galleryPayload, gallerySnapshot, initialGallery, mainCutoutPatch } from '@/lib/productGallery'
import { ProductCostSection, type DormantEstimate, type RecipeSummary } from './product/ProductCostSection'
import { CollapsibleRow } from './product/CollapsibleRow'
import apiClient from '@/api/client'
import { useCategories } from '@/hooks/useCategories'
import type { Product, ProductGalleryImage, ProductRecipe } from '@/types'
import { useCurrency } from '@/contexts/CurrencyContext'
import {
  applyCostToPayload,
  deriveCostMode,
  hasManualEstimate,
  type CostMode,
} from '@/lib/productFormPayload'
import { currencyInputPrefix, formatMoney } from '@/lib/formatMoney'
import { Lock } from 'lucide-react'

interface ProductFormProps {
  product?: Product // If provided, we're editing; otherwise creating
  onSuccess?: () => void
  onCancel?: () => void
  mode?: 'create' | 'edit'
}

type FormValues = CreateProductData | UpdateProductData

/**
 * Add / Edit Product (F-FE-N137 layout).
 *
 * One screen, three questions: what is it (Basics), what does it cost me
 * (Cost & profit — a single source: stock recipe, fixed amount, % of price or
 * none), and what is the cashier asked (option groups). Every field, guard and
 * API call from the previous layout is kept; the payload rules live in
 * lib/productFormPayload.ts where they are tested.
 */
export function ProductForm({ product, onSuccess, onCancel, mode = 'create' }: ProductFormProps) {
  const { currencyCode, formatCurrency } = useCurrency()
  const queryClient = useQueryClient()
  const isEditing = mode === 'edit' && product

  // Fetch categories for dropdown
  const { data: categories = [] } = useCategories()
  const categoryOptions = categories.map(cat => ({ value: cat.id.toString(), label: cat.name }))

  // The PCT/HS code a new product should carry, derived from its category.
  //
  // This field is read-only in the UI ("managed by the system"), and it used to
  // be hardcoded to '98017000' — the seed default, which is the FBR services
  // code for MESSES AND HOSTELS, not a food or beverage code at all. Because
  // the form always sent a non-empty value, the backend's category-default
  // lookup in createProduct never fired. That is how 35 products leaked onto it
  // on Karo and 8 on NinetyPlus, the latter filing PKR 23,100 under it before
  // anyone noticed (audit/NINETYPLUS_PCT_2026-08-03.md).
  //
  // Returns '' when the category has no usable default. The field then shows
  // the warning below and submits nothing, so the server decides — never this
  // form inventing a code.
  const pctForCategory = (categoryId: string): string => {
    const cat = categories.find(c => String(c.id) === String(categoryId))
    const code = cat?.default_pct_code?.trim() ?? ''
    return /^[0-9]{8}$/.test(code) ? code : ''
  }

  // The saved recipe, read-only here (F-FE-N157): it is edited in Costing &
  // Recipes, where the ingredient search, the guards and the variants live.
  // This form only says what exists and links across.
  const navigate = useNavigate()
  const { data: recipeData, isFetched: recipeFetched } = useQuery({
    queryKey: ['product-recipe', product?.id],
    queryFn: () => apiClient.getProductRecipe(product!.id).then(res => res.data as ProductRecipe),
    enabled: !!isEditing && !!product?.id,
  })
  const recipeSummary: RecipeSummary | null = isEditing
    ? {
        loaded: recipeFetched,
        count: recipeData?.components.length ?? 0,
        cost: recipeData?.recipe_cost ?? null,
        uncosted: recipeData?.uncosted ?? 0,
      }
    : null
  // Leaving for the recipe sheet discards this form, so unsaved product edits
  // are confirmed first (audit U-6). The ref is read at click time; the value
  // it mirrors is computed further down.
  const hasUnsavedChangesRef = useRef(false)
  const [leaveForRecipe, setLeaveForRecipe] = useState(false)
  const goToRecipeEditor = () => {
    if (!product?.id) return
    // Leave the form first: Menu Management swaps the whole page for this
    // form while a product is being edited, so the sheet would open behind it.
    onCancel?.()
    navigate({ to: '/admin/menu', search: { tab: 'costing', item: product.id } })
  }
  const openRecipeEditor = isEditing && product?.id
    ? () => {
        if (hasUnsavedChangesRef.current) setLeaveForRecipe(true)
        else goToRecipeEditor()
      }
    : undefined

  // Choose the appropriate schema and default values
  const schema = isEditing ? updateProductSchema : createProductSchema
  const existingHasCost = !!isEditing && hasManualEstimate(product)
  const defaultValues = isEditing
    ? {
        id: product.id,
        name: product.name,
        description: product.description || '',
        price: product.price,
        category_id: product.category_id != null ? String(product.category_id) : '',
        image_url: product.image_url || '',
        preparation_time: product.preparation_time || 5,
        pct_code: product.pct_code || '98017000',
        fbr_uom: product.fbr_uom ?? '',
        fbr_sale_type: product.fbr_sale_type ?? '',
        fbr_sro_schedule_no: product.fbr_sro_schedule_no ?? '',
        fbr_sro_item_serial_no: product.fbr_sro_item_serial_no ?? '',
        fbr_fed_payable: product.fbr_fed_payable ?? 0,
        projected_cost_basis: existingHasCost ? (product.projected_cost_basis ?? 'amount') : undefined,
        projected_cost: product.projected_cost ?? undefined,
        projected_cost_percent: product.projected_cost_percent ?? undefined,
        tracked_stock_item_id: product.tracked_stock_item_id ?? null,
        stock_deduct_qty: product.stock_deduct_qty ?? 1,
      }
    : {
        name: '',
        description: '',
        price: 0,
        category_id: categories[0]?.id != null ? String(categories[0].id) : '',
        image_url: '',
        preparation_time: 5,
        is_available: true,
        pct_code: pctForCategory(categories[0]?.id != null ? String(categories[0].id) : ''),
        fbr_uom: '',
        fbr_sale_type: '',
        fbr_sro_schedule_no: '',
        fbr_sro_item_serial_no: '',
        fbr_fed_payable: 0,
        projected_cost_basis: undefined,
        projected_cost: undefined,
        projected_cost_percent: undefined,
        tracked_stock_item_id: null,
        stock_deduct_qty: 1,
      }

  const form = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues,
  })

  const imagePreviewUrl = form.watch('image_url')?.trim() || ''

  // "More photos" (storefront hover float). Held outside react-hook-form, like
  // the variants, and attached at submit; the zod schema would strip it.
  const [gallery, setGallery] = useState<ProductGalleryImage[]>(() =>
    initialGallery(isEditing ? product.gallery_images : null),
  )
  const [galleryBaseline] = useState(() => gallerySnapshot(initialGallery(isEditing ? product.gallery_images : null)))
  const watchedName = form.watch('name') ?? ''
  const watchedDescription = form.watch('description') ?? ''
  const watchedPrepTime = form.watch('preparation_time') ?? 5

  // Keep the PCT code following the selected category while creating, so the
  // read-only line shows the code the product will actually be filed under
  // rather than a constant the operator cannot see is wrong.
  //
  // Create only. Editing must never silently re-code an existing product just
  // because someone moved it between categories — that is a classification
  // change and belongs in a reviewed script, not a side effect of a menu tidy.
  const watchedCategoryId = form.watch('category_id')
  const categoriesLoaded = categories.length > 0
  useEffect(() => {
    if (isEditing) return
    if (!categoriesLoaded) return
    const next = pctForCategory(String(watchedCategoryId ?? ''))
    if ((form.getValues('pct_code') ?? '') !== next) {
      form.setValue('pct_code', next, { shouldDirty: false })
    }
    // pctForCategory closes over `categories`; categoriesLoaded gates the first
    // run until they arrive, and the id + length pair covers every later change.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [watchedCategoryId, categoriesLoaded, categories.length, isEditing])

  // A category with no usable default is the exact condition that generated the
  // Karo leak — Bakery, The Pudding Co and Shakes were all created with a blank
  // default_pct_code, and all 26 products under them inherited the seed value.
  // Surface it at the point of creation instead of discovering it in an audit.
  const categoryHasNoPctDefault =
    !isEditing && categoriesLoaded && pctForCategory(String(watchedCategoryId ?? '')) === ''
  const watchedPctCode = form.watch('pct_code') ?? ''
  const selectedCategory = categories.find(c => String(c.id) === String(watchedCategoryId ?? ''))

  // ---- Cost source -------------------------------------------------------
  // One choice: recipe / fixed amount / % of price / none. The server stores a
  // recipe and a manual estimate side by side and the sale engine prefers the
  // recipe, so when both exist the recipe is the one in force and the manual
  // figure is shown as dormant with an explicit "Remove it".
  const [costMode, setCostMode] = useState<CostMode>(() => deriveCostMode(product))
  const [costOpen, setCostOpen] = useState(() => deriveCostMode(product) !== 'none')
  const [removeDormant, setRemoveDormant] = useState(false)
  const dormantEstimate: DormantEstimate | null =
    costMode === 'recipe' && existingHasCost && product
      ? product.projected_cost_basis === 'percent'
        ? { basis: 'percent', value: product.projected_cost_percent ?? null }
        : { basis: 'amount', value: product.projected_cost ?? null }
      : null

  const watchedPrice = form.watch('price') ?? 0
  const watchedCost = form.watch('projected_cost')
  const watchedPercent = form.watch('projected_cost_percent')

  // Live margin preview for the manual estimate — derived from current form
  // values so the user sees exactly what will be persisted before saving.
  // Mirrors normalizeProjectedCost on the server so the two stay in sync.
  const manualPreview = useMemo(() => {
    if (costMode !== 'amount' && costMode !== 'percent') return null
    const price = Number(watchedPrice) || 0
    let cost: number | null = null
    if (costMode === 'amount') {
      cost = watchedCost == null ? null : Number(watchedCost)
    } else {
      const pct = watchedPercent == null ? null : Number(watchedPercent)
      cost = pct == null ? null : (price * pct) / 100
    }
    if (cost == null || Number.isNaN(cost)) return null
    const margin = price - cost
    const marginPct = price > 0 ? (margin / price) * 100 : 0
    return { cost, margin, marginPct }
  }, [costMode, watchedPrice, watchedCost, watchedPercent])

  const setBasisMode = (basis: 'amount' | 'percent') => {
    form.setValue('projected_cost_basis', basis, { shouldDirty: true })
    if (basis === 'amount') {
      form.setValue('projected_cost_percent', null, { shouldDirty: true })
    } else {
      form.setValue('projected_cost', null, { shouldDirty: true })
    }
  }

  const clearCosting = () => {
    form.setValue('projected_cost_basis', undefined, { shouldDirty: true })
    form.setValue('projected_cost', null, { shouldDirty: true })
    form.setValue('projected_cost_percent', null, { shouldDirty: true })
  }

  const changeCostMode = (next: CostMode) => {
    if (next === costMode) return
    setCostMode(next)
    setRemoveDormant(false)
    if (next === 'recipe') return
    if (next === 'none') clearCosting()
    else setBasisMode(next)
  }

  // ---- Sizes & questions (F-MOD-N12) ---------------------------------------
  // Kept out of the RHF schema and written by a side call after the product
  // saves, the same shape the form already uses for stock components and the
  // category form uses for its kitchen station — so the product create/update
  // handler stays untouched. lib/productVariants.ts owns the mapping onto
  // modifier groups and attachments.
  const [variants, setVariants] = useState<VariantsState>(EMPTY_VARIANTS)
  const [variantsBaseline, setVariantsBaseline] = useState(() => variantsSnapshot(EMPTY_VARIANTS))
  const onVariantsLoaded = (s: VariantsState) => {
    setVariants(s)
    setVariantsBaseline(variantsSnapshot(s))
  }

  /**
   * Persist sizes, questions and the attachment.
   *
   * Never throws. It runs inside a mutation's onSuccess, and an escaping
   * rejection there would skip the cache invalidations and the success toast
   * that follow — so a product that saved perfectly well would report as a
   * failure and leave stale lists on screen.
   */
  const writeProductVariants = async (productId: string, productName: string) => {
    try {
      await persistVariants(productId, productName, variants)
    } catch (err) {
      toastHelpers.error(
        'Product saved, but its sizes and questions were not',
        err instanceof Error ? err.message : 'Unknown error',
      )
    } finally {
      queryClient.invalidateQueries({ queryKey: ['product-modifier-groups'] })
      queryClient.invalidateQueries({ queryKey: ['modifier-groups'] })
      queryClient.invalidateQueries({ queryKey: ['modifier-attachments'] })
      queryClient.invalidateQueries({ queryKey: ['variant-usage'] })
      queryClient.invalidateQueries({ queryKey: ['menu-modifiers'] })
    }
  }

  const createMutation = useMutation({
    mutationFn: (data: CreateProductData) => apiClient.createProduct(data),
    onSuccess: async (response) => {
      const newId = (response as { data?: { id?: string } })?.data?.id
      if (newId) await writeProductVariants(newId, form.getValues('name') ?? '')
      queryClient.invalidateQueries({ queryKey: ['admin-products'] })
      queryClient.invalidateQueries({ queryKey: ['products'] })
      queryClient.invalidateQueries({ queryKey: ['categories'] })
      toastHelpers.productCreated(form.getValues('name') ?? '')
      const openRecipe = costMode === 'recipe' && Boolean(newId)
      form.reset()
      onSuccess?.()
      // "From ingredients" on a new product: the recipe can only be built once
      // the product exists, so take the owner straight to it (audit U-14).
      if (openRecipe && newId) {
        navigate({ to: '/admin/menu', search: { tab: 'costing', item: newId } })
      }
    },
    onError: (error) => {
      toastHelpers.apiError('Create product', error)
    },
  })

  const updateMutation = useMutation({
    mutationFn: (data: UpdateProductData) => apiClient.updateProduct(data.id.toString(), data),
    onSuccess: async () => {
      if (product?.id) await writeProductVariants(product.id, form.getValues('name') ?? '')
      queryClient.invalidateQueries({ queryKey: ['admin-products'] })
      queryClient.invalidateQueries({ queryKey: ['products'] })
      queryClient.invalidateQueries({ queryKey: ['categories'] })
      queryClient.invalidateQueries({ queryKey: ['product-recipe', product?.id] })
      toastHelpers.apiSuccess('Update', `Product "${form.getValues('name') ?? ''}"`)
      onSuccess?.()
    },
    onError: (error) => {
      toastHelpers.apiError('Update product', error)
    },
  })

  const onSubmit = (data: FormValues) => {
    // Submit-time guard for the manual estimate. The schema is intentionally
    // permissive (so an untouched cost stays untouched on the server), but if
    // the operator picked a manual source they must enter a value.
    if (costMode === 'amount' || costMode === 'percent') {
      const basis = data.projected_cost_basis ?? costMode
      if (basis === 'amount' && (data.projected_cost == null || Number.isNaN(Number(data.projected_cost)))) {
        form.setError('projected_cost', { type: 'manual', message: 'Enter a cost amount or switch to "% of price"' })
        setCostOpen(true)
        return
      }
      if (basis === 'percent' && (data.projected_cost_percent == null || Number.isNaN(Number(data.projected_cost_percent)))) {
        form.setError('projected_cost_percent', { type: 'manual', message: 'Enter a percent (0-100)' })
        setCostOpen(true)
        return
      }
    }

    // Sizes and questions must be complete before anything is written.
    const variantProblem = variantsProblem(variants)
    if (variantProblem) {
      toastHelpers.error('Finish the sizes and questions first', variantProblem)
      return
    }
    // "By size": the item's listed price is the lowest size, so lists,
    // reports and the counter's price label all have one number to show.
    const sizeBase = basePriceFromSizes(variants.sizes)
    const submitData = sizeBase != null ? { ...data, price: sizeBase } : data

    // The recipe itself is never sent from here (F-FE-N157): it is owned by
    // Costing & Recipes. applyCostToPayload only shapes the manual trio.
    const costPayload = applyCostToPayload(submitData, {
      mode: costMode,
      existingHasCost,
      removeDormantEstimate: removeDormant,
    })

    // Extra photos are always sent (`[]` clears them). The main photo's
    // cutout is never edited here: it is omitted (kept) unless the main photo
    // was replaced or removed, then cleared so a stale cutout cannot linger.
    const photoFields = {
      gallery_images: galleryPayload(gallery),
      ...mainCutoutPatch(!!isEditing, product?.image_url, data.image_url),
    }

    // Tags (product_tags) are assigned tag-centrically from Reports → Items →
    // Tag now, not here. We deliberately omit tag_ids so a product edit leaves
    // the product's existing tag membership untouched (updateProduct treats a
    // nil tag_ids as "don't change").

    if (isEditing) {
      updateMutation.mutate({ ...(costPayload as UpdateProductData), ...photoFields })
    } else {
      createMutation.mutate({ ...(costPayload as CreateProductData), ...photoFields })
    }
  }

  const isLoading = createMutation.isPending || updateMutation.isPending
  const [detailsOpen, setDetailsOpen] = useState(!isEditing)

  const hasUnsavedChanges =
    form.formState.isDirty ||
    variantsSnapshot(variants) !== variantsBaseline ||
    costMode !== deriveCostMode(product) ||
    removeDormant ||
    gallerySnapshot(gallery) !== galleryBaseline
  hasUnsavedChangesRef.current = hasUnsavedChanges

  if (categories.length === 0) {
    return (
      <Card className="w-full max-w-4xl mx-auto">
        <CardContent className="pt-6">
          <div className="text-center py-8">
            <p className="text-muted-foreground mb-4">
              You need to create at least one category before adding products.
            </p>
            <Button onClick={onCancel} variant="outline">
              Go Back
            </Button>
          </div>
        </CardContent>
      </Card>
    )
  }

  const detailsSummary = [
    watchedDescription.trim() ? 'Description added' : 'No description',
    `Prep time ${watchedPrepTime} min`,
  ].join(' · ')

  return (
    <Card className="w-full max-w-4xl mx-auto">
      <ProductIdentityHeader
        mode={isEditing ? 'edit' : 'create'}
        name={watchedName}
        imageUrl={imagePreviewUrl}
        categoryName={selectedCategory?.name}
        priceLabel={formatMoney(Number(watchedPrice) || 0, currencyCode)}
        trackedInInventory={isEditing ? (recipeSummary?.count ?? 0) > 0 : false}
        onClose={onCancel}
        closeDisabled={isLoading}
      />
      <CardContent className="p-0">
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)}>
            <div className="px-6">
              {/* Basics */}
              <div className="space-y-5 py-5">
                <ProductPhotoField
                  control={form.control}
                  name="image_url"
                  imageUrl={imagePreviewUrl}
                  setValue={form.setValue}
                />
                <ProductGalleryField value={gallery} onChange={setGallery} />
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-[2fr_1fr]">
                  <TextInputField
                    control={form.control}
                    name="name"
                    label="Name"
                    placeholder="What the menu calls it"
                  />
                  {variants.sizes ? (
                    <div className="space-y-1.5">
                      <p className="text-sm font-medium">Price</p>
                      <p className="flex h-10 items-center rounded-md border bg-muted/40 px-3 text-sm tabular-nums text-muted-foreground">
                        By size · from {formatMoney(basePriceFromSizes(variants.sizes) ?? 0, currencyCode)}
                      </p>
                    </div>
                  ) : (
                    <PriceInputField
                      control={form.control}
                      name="price"
                      label="Price"
                      currency={currencyInputPrefix(currencyCode)}
                    />
                  )}
                </div>
                <div className="space-y-1.5">
                  <SelectField
                    control={form.control}
                    name="category_id"
                    label="Category"
                    options={categoryOptions}
                    placeholder="Choose a category"
                  />
                  {/* The PCT/HS code stays a form value (hidden) so the create
                      effect above and the schema contract keep working; on
                      screen it is one quiet fact, not a warning box. */}
                  <input type="hidden" {...form.register('pct_code')} />
                  <p className="flex items-start gap-1.5 text-xs text-muted-foreground">
                    <Lock className="mt-0.5 h-3 w-3 shrink-0" aria-hidden />
                    <span>
                      {isEditing ? (
                        <>
                          Tax code <span className="font-medium tabular-nums text-foreground">{watchedPctCode || '—'}</span>, managed by the system. Contact support to change it.
                        </>
                      ) : categoryHasNoPctDefault ? (
                        <>Tax code: none set on this category.</>
                      ) : (
                        <>
                          Tax code <span className="font-medium tabular-nums text-foreground">{watchedPctCode}</span>, inherited from the category. Change it on the category, not here.
                        </>
                      )}
                    </span>
                  </p>
                  {categoryHasNoPctDefault && (
                    <p className="rounded-md border border-amber-200 bg-amber-50/60 p-2.5 text-xs font-medium text-amber-800 dark:border-amber-900/60 dark:bg-amber-950/20 dark:text-amber-300">
                      This category has no default HS/PCT code set, so this product will fall back to
                      the system default — which is a services code, not a food or beverage one. Set
                      the category's default before creating items under it, or this product will be
                      filed under the wrong code.
                    </p>
                  )}
                </div>
              </div>

              {/* More details — folded; rarely touched after creation */}
              <CollapsibleRow
                id="product-details-panel"
                title="More details"
                summary={detailsSummary}
                open={detailsOpen}
                onToggle={() => setDetailsOpen(v => !v)}
              >
                <TextareaField
                  control={form.control}
                  name="description"
                  label="Description"
                  placeholder="Shown to staff and on the online menu"
                  rows={3}
                />
                <div className="max-w-xs">
                  <NumberInputField
                    control={form.control}
                    name="preparation_time"
                    label="Prep time (minutes)"
                    min={1}
                    max={120}
                  />
                </div>
              </CollapsibleRow>

              {/* Cost & profit — one source */}
              <ProductCostSection
                open={costOpen}
                onToggle={() => setCostOpen(v => !v)}
                control={form.control}
                currencyPrefix={currencyInputPrefix(currencyCode)}
                formatCurrency={formatCurrency}
                price={Number(watchedPrice) || 0}
                mode={costMode}
                onModeChange={changeCostMode}
                manualPreview={manualPreview}
                recipeSummary={recipeSummary}
                onEditRecipe={openRecipeEditor}
                dormantEstimate={dormantEstimate}
                removeDormant={removeDormant}
                onToggleRemoveDormant={() => setRemoveDormant(v => !v)}
              />

              {/* Sizes & questions (F-MOD-N12) — what the till asks when this
                  item is tapped. Empty means a single tap adds it, as now. */}
              <div className="border-t py-5">
                <ProductVariantsSection
                  productId={product?.id}
                  productName={watchedName}
                  basePrice={Number(watchedPrice) || 0}
                  categoryId={form.watch('category_id')}
                  categoryName={selectedCategory?.name}
                  state={variants}
                  onChange={setVariants}
                  onLoaded={onVariantsLoaded}
                  formatCurrency={formatCurrency}
                  currencyPrefix={currencyInputPrefix(currencyCode)}
                  onePriceField={
                    <p className="text-xs text-muted-foreground">
                      Uses the Price above. Switch to “By size” to sell it in sizes with their own prices.
                    </p>
                  }
                  onOpenCosting={openRecipeEditor}
                />
              </div>

              {/* F-MOD-N13: this item's own prices on shared option lists. Only
                  for a saved product — overrides key on the product id. */}
              {product?.id && (
                <div className="border-t py-5">
                  <ItemOptionPrices productId={product.id} productName={watchedName} formatCurrency={formatCurrency} />
                </div>
              )}
            </div>

            <Dialog open={leaveForRecipe} onOpenChange={setLeaveForRecipe}>
              <DialogContent className="sm:max-w-md">
                <DialogHeader>
                  <DialogTitle>Leave without saving?</DialogTitle>
                  <DialogDescription>
                    The recipe opens in Costing &amp; Recipes. Your unsaved changes to this product will be lost.
                  </DialogDescription>
                </DialogHeader>
                <DialogFooter>
                  <Button type="button" variant="outline" onClick={() => setLeaveForRecipe(false)}>
                    Keep editing
                  </Button>
                  <Button
                    type="button"
                    variant="destructive"
                    onClick={() => {
                      setLeaveForRecipe(false)
                      goToRecipeEditor()
                    }}
                  >
                    Leave
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>

            {/* Sticky footer: Save is always reachable on a long form. */}
            <div className="sticky bottom-0 flex items-center gap-2 rounded-b-lg border-t bg-card/95 px-6 py-3 backdrop-blur supports-[backdrop-filter]:bg-card/80">
              <p className="flex-1 text-xs text-muted-foreground" aria-live="polite">
                {hasUnsavedChanges ? 'Unsaved changes' : ''}
              </p>
              {onCancel && (
                <Button type="button" variant="ghost" onClick={onCancel} disabled={isLoading}>
                  Cancel
                </Button>
              )}
              <FormSubmitButton isLoading={isLoading} loadingText="Saving…">
                {isEditing ? 'Save changes' : 'Create product'}
              </FormSubmitButton>
            </div>
          </form>
        </Form>
      </CardContent>
    </Card>
  )
}
